/* Fit filter on parts, accessories and tips collections (snippets/fit-filter.liquid).
   "Fits your sprayer" reads each card's data-fit-models (ase.fits_models), "Fits your handpiece" its data-fit-hp
   (ase.fits_handpiece). OR within a group, AND across groups, and AND with whatever plp.js already filters on the page
   (stock and segments set li.hidden, the paint picker sets data-paint-out; price and storefront filters come back from
   the server). This file hides cards with its own data-fit-out attribute, so it never fights plp.js over li.hidden, and
   then owns the shared result count, the segment counts, the stock counts and the empty state while a fit is picked.
   State: ?model=ultra-390,ultra-495-xt&handpiece=contractor-pc, remembered for the visit in sessionStorage (the URL wins).
   The paint picker's sprayer step ([data-pt-sprayer], tip pages) and "Your sprayer" follow each other.
   A collection bigger than one page (spare-parts, 342 at 250 a page) has its other pages fetched and added to the grid,
   so the filter always works on the whole collection. Loads after plp.js so its listeners run after plp.js's. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const KEY = 'ase.fit';
  const GROUPS = ['model', 'handpiece'];
  const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  /* Sprayer title to the painter's model name: the same rules as MODEL_RULES in ops/lib/fit_models.mjs, which wrote
     ase.fits_models (ops/test/fit_groups.test.mjs checks the two agree) */
  const RULES = [[/Ultra QuickShot II/, 'Ultra QuickShot II'], [/Ultra QuickShot/, 'Ultra QuickShot'], [/Ultra Corded Handheld/, 'Ultra Corded Handheld'],
    [/Ultra Max Cordless Handheld/, 'Ultra Max Cordless Handheld'], [/Ultra Cordless Handheld/, 'Ultra Cordless Handheld'],
    [/390 PC Cordless/, '390 PC Cordless'], [/FinishPro GX 19 Cordless/, 'FinishPro GX 19 Cordless'], [/FinishPro GX 19\b/, 'FinishPro GX 19'], [/\bGX 19\b/, 'GX 19'],
    [/Mark V XT HD/, 'Mark V XT HD'], [/GMAX II 3900/, 'GMAX II 3900'], [/GMAX II 5900/, 'GMAX II 5900'],
    [/Ultra (\d{3,4}) XT/, (m) => `Ultra ${m[1]} XT`], [/Ultra (\d{3})\b/, (m) => `Ultra ${m[1]}`]];
  const modelOfTitle = (t) => {
    for (const [re, out] of RULES) { const m = String(t).match(re); if (m) return typeof out === 'function' ? out(m) : out; }
    return null;
  };
  const list = (v) => new Set(String(v || '').split(',').map((x) => x.trim()).filter(Boolean));

  $$('[data-plp]').forEach((root) => {
    const panel = $('[data-ff-panel]', root);
    const bar = $('[data-ff-bar]', root);
    if (!panel && !bar) return;
    const sid = root.dataset.sectionId;
    const state = { model: new Set(), handpiece: new Set() };
    const names = {};
    $$('[data-ff-input]', root).forEach((i) => { names[`${i.dataset.ffGroup}:${i.value}`] = i.dataset.ffName; });
    const active = () => state.model.size > 0 || state.handpiece.size > 0;
    /* One machine's own parts or tips page (graco-ultra-490-xt-parts, graco-ultra-695-795-xt-tips-and-accessories): the
       page already is that sprayer, so "Your sprayer" offers only the page's own machines (none to pick when it is one)
       and a sprayer remembered from another page is never applied here. */
    const pm = decodeURIComponent(window.location.pathname).match(/\/collections\/graco-(.+)-(?:parts|tips-and-accessories)\/?$/);
    const machinePage = !!pm;
    const machines = (() => {
      if (!pm) return [];
      const page = new Set(pm[1].split('-'));
      return Object.keys(names).filter((k) => k.startsWith('model:')).map((k) => k.slice(6)).filter((v) => {
        const t = v.split('-');
        return t.every((x) => page.has(x)) || [...page].every((x) => t.includes(x));
      });
    })();
    const known = (g, v) => !!names[`${g}:${v}`] && (g !== 'model' || !machines.length || machines.includes(v));
    /* A value the page has no option for: try the XT name (ultra-490 is the Ultra 490 XT), otherwise drop it */
    const clean = (g, set) => new Set([...set].map((v) => (known(g, v) ? v : (known(g, `${v}-xt`) ? `${v}-xt` : ''))).filter(Boolean));
    const cards = () => $$('.plp-results .plp-grid > li', root);

    /* Each card's fits, read once per element (re-rendered cards are new elements) */
    const fitsOf = (li) => {
      if (!li._ff) {
        const c = $('[data-product-card]', li) || li;
        const read = (a) => new Set(String(c.getAttribute(a) || '').split('|').filter(Boolean).map(slug));
        li._ff = { model: read('data-fit-models'), handpiece: read('data-fit-hp') };
      }
      return li._ff;
    };
    const fitsGroup = (li, g, set) => !set.size || [...set].some((v) => fitsOf(li)[g].has(v));
    const fitsAll = (li, skip) => GROUPS.every((g) => g === skip || fitsGroup(li, g, state[g]));
    /* Visible under plp.js's own filters (stock, segments, paint) */
    const base = (li) => !li.hidden && !li.hasAttribute('data-paint-out');

    /* ---------- state in and out ---------- */
    const fromUrl = () => {
      const q = new URLSearchParams(window.location.search);
      return { has: q.has('model') || q.has('handpiece'), model: clean('model', list(q.get('model'))), handpiece: clean('handpiece', list(q.get('handpiece'))) };
    };
    const writeParams = (params) => {
      GROUPS.forEach((g) => { if (state[g].size) params.set(g, [...state[g]].join(',')); else params.delete(g); });
    };
    const toUrl = () => {
      const u = new URL(window.location.href);
      writeParams(u.searchParams);
      const next = u.pathname + u.search + u.hash;
      if (next !== window.location.pathname + window.location.search + window.location.hash) history.replaceState(history.state, '', next);
    };
    const remember = () => {
      try {
        if (active()) sessionStorage.setItem(KEY, JSON.stringify({ model: [...state.model], handpiece: [...state.handpiece] }));
        else sessionStorage.removeItem(KEY);
      } catch (e) { /* storage blocked */ }
    };
    const recalled = () => {
      try { const v = JSON.parse(sessionStorage.getItem(KEY) || 'null'); return v && typeof v === 'object' ? v : null; } catch (e) { return null; }
    };

    /* ---------- paint picker's sprayer step ---------- */
    const pt = $('[data-pt-sprayer]', root);
    let syncing = false;
    const ptModel = () => {
      if (!pt || !pt.value) return '';
      const o = pt.options[pt.selectedIndex];
      return o ? slug(modelOfTitle(o.textContent)) : '';
    };
    const pushToPt = () => {
      if (!pt || syncing) return;
      /* Several sprayers picked: the paint picker goes back to any sprayer rather than keeping the first one's max tip */
      const want = state.model.size === 1 ? [...state.model][0] : '';
      if (ptModel() === want) return;
      let value = '';
      if (want) {
        const o = Array.from(pt.options).find((x) => x.value && slug(modelOfTitle(x.textContent)) === want);
        if (!o) return;
        value = o.value;
      }
      if (pt.value === value) return;
      syncing = true;
      pt.value = value;
      pt.dispatchEvent(new Event('change', { bubbles: true }));
      syncing = false;
    };
    if (pt) {
      pt.addEventListener('change', () => {
        if (syncing) return;
        const m = ptModel();
        if (m && !known('model', m)) return; /* a sprayer nothing on this page lists: leave the fit filter alone */
        state.model = m ? new Set([m]) : new Set();
        commit({ fromPt: true });
      });
    }

    /* ---------- render ---------- */
    function setCount(visible, lis) {
      $$('[data-plp-n]', root).forEach((el) => {
        if (!el.dataset.total) el.dataset.total = el.textContent;
        const total = parseInt(el.dataset.total, 10);
        const v = active() || visible < lis.length ? visible : total;
        el.textContent = v;
        const unit = el.parentElement && $('[data-plp-unit]', el.parentElement);
        if (unit) unit.textContent = v === 1 ? 'product' : 'products';
      });
    }

    /* Cards that pass the fit picks, stock and the paint picker, whatever segment is showing, counted per segment */
    const segTally = (lis) => {
      const stock = $$('[data-stock-input]', root).filter((i) => i.checked).map((i) => i.value);
      const t = { '': 0 };
      lis.forEach((li) => {
        if (!li.dataset.seg || li.hasAttribute('data-paint-out') || !fitsAll(li)) return;
        if (stock.length && !stock.includes(li.dataset.stock)) return;
        t[li.dataset.seg] = (t[li.dataset.seg] || 0) + 1;
        t[''] += 1;
      });
      return t;
    };
    function segCounts(lis) {
      if (!root.hasAttribute('data-seg-client')) return;
      const t = segTally(lis);
      $$('[data-seg-chip]', root).forEach((c) => {
        const n = $('.qchip__n', c);
        if (!n) return;
        if (n.dataset.ffBase === undefined) n.dataset.ffBase = n.textContent;
        const k = t[c.dataset.segChip] || 0;
        n.textContent = active() ? k : n.dataset.ffBase;
        c.classList.toggle('is-off', active() && k === 0 && !c.classList.contains('is-on'));
      });
    }

    function emptyState(visible) {
      const results = $('.plp-results', root);
      if (!results) return;
      let box = $('[data-ff-empty]', results);
      const show = active() && visible === 0 && cards().length > 0;
      $$('[data-seg-empty]', results).forEach((e) => { if (active()) e.hidden = true; });
      if (!show) { if (box) box.hidden = true; return; }
      if (!box) {
        box = document.createElement('div');
        box.className = 'plp-empty';
        box.setAttribute('data-ff-empty', '');
        box.innerHTML = '<h2 class="plp-empty__title"></h2><p data-ff-empty-what></p><p data-ff-empty-seg hidden></p><p data-ff-empty-try></p><p class="plp-empty__help" data-ff-empty-help></p>'
          + '<button type="button" class="btn btn--sm ff-empty__clear" data-ff-clear>Clear sprayer and handpiece</button>';
        results.appendChild(box);
      }
      const picks = GROUPS.map((g) => {
        const n = [...state[g]].map((v) => names[`${g}:${v}`] || v).join(' or ');
        return n ? `the ${n}${g === 'handpiece' ? ' gun' : ''}` : '';
      }).filter(Boolean);
      const stockOn = $$('[data-stock-input]', root).some((i) => i.checked) || /filter\.p\.m\.ase\.stock_status/.test(window.location.search);
      const priceOn = $$('[data-price-input]', root).some((i) => String(i.value).trim() !== '');
      const paintOn = /[?&]paint=/.test(window.location.search);
      const segOn = $$('[data-seg-input]', root).some((i) => i.checked) || /[?&]segment=/.test(window.location.search);
      const n = state.model.size + state.handpiece.size + (stockOn ? 1 : 0) + (priceOn ? 1 : 0) + (segOn ? 1 : 0) + (paintOn ? 1 : 0);
      $('.plp-empty__title', box).textContent = n > 1 ? 'Nothing here fits all of those.' : 'Nothing here fits that.';
      $('[data-ff-empty-what]', box).textContent = `Nothing in this list fits ${picks.length > 1 ? 'both ' : ''}${picks.join(' and ')}${stockOn ? ' and is in stock' : ''}.`;
      const tries = [];
      if (state.model.size && state.handpiece.size) tries.push('take the handpiece off');
      tries.push(state.model.size ? 'pick another sprayer' : 'pick another handpiece');
      if (stockOn) tries.push('untick In stock');
      if (priceOn) tries.push('widen the price');
      if (segOn) tries.push('pick All parts');
      if (paintOn) tries.push('clear the paint');
      const t = tries.join(', ').replace(/, ([^,]*)$/, ' or $1');
      $('[data-ff-empty-try]', box).textContent = `Try to ${t}.`;
      /* The part is on this page, under another segment (a gun filter sits in Handpiece parts, not Filters) */
      const segP = $('[data-ff-empty-seg]', box);
      segP.textContent = '';
      const tally = segOn ? segTally(cards()) : {};
      const elsewhere = $$('[data-seg-chip]', root).filter((c) => c.dataset.segChip && (tally[c.dataset.segChip] || 0) > 0 && !c.classList.contains('is-on'));
      if (elsewhere.length) {
        segP.append('What fits is under ');
        elsewhere.forEach((c, i) => {
          if (i) segP.append(i === elsewhere.length - 1 ? ' and ' : ', ');
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ff-empty__seg';
          b.dataset.ffSeg = c.dataset.segChip;
          b.textContent = `${c.dataset.segLabel || c.dataset.segChip} (${tally[c.dataset.segChip]})`;
          segP.appendChild(b);
        });
        segP.append('.');
      }
      segP.hidden = !elsewhere.length;
      const help = $('[data-ff-empty-help]', box);
      const mail = (bar && bar.dataset.ffEmail) || '';
      help.textContent = '';
      if (mail) {
        help.append('Not sure what fits? Email the model or a photo to ');
        const a = document.createElement('a');
        a.href = `mailto:${mail}`;
        a.textContent = mail;
        help.append(a, " and we'll find it.");
      } else help.textContent = "Not sure what fits? Email us the model or a photo and we'll find it.";
      box.hidden = false;
    }

    function apply() {
      const lis = cards();
      lis.forEach((li) => { if (fitsAll(li)) li.removeAttribute('data-fit-out'); else li.setAttribute('data-fit-out', ''); });

      /* Live counts: each option counts the cards that pass plp.js's filters and every OTHER fit group, plus itself */
      const seen = { model: new Set(), handpiece: new Set() };
      lis.forEach((li) => GROUPS.forEach((g) => fitsOf(li)[g].forEach((v) => seen[g].add(v))));
      const counts = {};
      GROUPS.forEach((g) => {
        const pool = lis.filter((li) => base(li) && fitsAll(li, g));
        Object.keys(names).filter((k) => k.startsWith(`${g}:`)).forEach((k) => {
          const v = k.slice(g.length + 1);
          counts[k] = pool.filter((li) => fitsOf(li)[g].has(v)).length;
        });
      });
      Object.keys(names).forEach((k) => {
        const [g, v] = [k.slice(0, k.indexOf(':')), k.slice(k.indexOf(':') + 1)];
        const on = state[g].has(v);
        const n = counts[k] || 0;
        const offer = (on || seen[g].has(v)) && known(g, v);
        $$(`[data-ff-opt="${k}"]`, root).forEach((el) => {
          el.hidden = !offer;
          if (el.tagName === 'OPTION') {
            el.disabled = !offer || (n === 0 && !on);
            el.textContent = `${names[k]} · ${n}`;
          } else if (el.matches('[data-ff-chip]')) {
            el.classList.toggle('is-on', on);
            el.classList.toggle('is-off', n === 0 && !on);
            el.setAttribute('aria-pressed', String(on));
            const c = $('[data-ff-n]', el); if (c) c.textContent = n;
          } else {
            const i = $('[data-ff-input]', el);
            if (i) { i.checked = on; i.disabled = n === 0 && !on; }
            const c = $('[data-ff-n]', el); if (c) c.textContent = n;
          }
        });
      });
      $$('[data-ff-range]', root).forEach((r) => { r.hidden = !$$('[data-ff-opt]', r).some((o) => !o.hidden); });
      $$('[data-ff-box]', root).forEach((b) => {
        b.hidden = !$$('[data-ff-opt]', b).some((o) => !o.hidden) || (b.dataset.ffBox === 'model' && machines.length === 1 && !state.model.size);
        const gn = $('[data-ff-gn]', b);
        const k = state[b.dataset.ffBox].size;
        if (gn) { gn.hidden = k === 0; gn.textContent = k ? k : ''; }
      });

      /* "Your sprayer": one pick shows it, several say how many */
      const sel = $('[data-ff-select]', root);
      if (sel) {
        const multi = $('[data-ff-multi]', sel);
        if (state.model.size > 1) {
          multi.textContent = `${state.model.size} sprayers picked`;
          multi.hidden = false;
          sel.value = '__multi';
        } else {
          multi.hidden = true;
          sel.value = state.model.size === 1 ? [...state.model][0] : '';
        }
      }

      /* Applied picks above the grid, each removable, then Clear */
      const act = $('[data-ff-active]', root);
      if (act) {
        act.textContent = '';
        GROUPS.forEach((g) => state[g].forEach((v) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'achip';
          b.dataset.ffRemove = `${g}:${v}`;
          const t = document.createElement('span');
          t.textContent = `${g === 'model' ? 'Fits ' : 'Fits the '}${names[`${g}:${v}`] || v}${g === 'handpiece' ? ' gun' : ''}`;
          const x = document.createElement('span');
          x.className = 'achip__x';
          x.setAttribute('aria-hidden', 'true');
          x.innerHTML = X;
          const h = document.createElement('span');
          h.className = 'visually-hidden';
          h.textContent = 'Remove filter';
          b.append(t, x, h);
          act.appendChild(b);
        }));
        if (active()) {
          const c = document.createElement('button');
          c.type = 'button';
          c.className = 'active__clear';
          c.dataset.ffClear = '';
          c.textContent = 'Clear';
          act.appendChild(c);
        }
        act.hidden = !active();
      }
      $$('.ff__clear', root).forEach((b) => { b.hidden = !active(); });

      /* Filter button badge: the server's own count plus the fit picks */
      $$('[data-plp-region="fcount"]', root).forEach((el) => {
        if (el.dataset.ffBase === undefined) el.dataset.ffBase = el.textContent.trim();
        const k = (parseInt(el.dataset.ffBase, 10) || 0) + state.model.size + state.handpiece.size;
        el.textContent = k ? k : '';
      });

      /* Stock counts (client stock group) follow the fit picks and every loaded card */
      $$('[data-stock-input]', root).forEach((i) => {
        const n = lis.filter((li) => li.dataset.stock === i.value && !li.hasAttribute('data-paint-out') && fitsAll(li)).length;
        const c = i.nextElementSibling && $('.fv__n', i.nextElementSibling);
        if (c) c.textContent = n;
        i.disabled = n === 0 && !i.checked;
      });

      /* One sprayer's parts: segment headings count what's left in them */
      $$('[data-seg-group]', root).forEach((g) => {
        const vis = $$('li[data-seg]', g).filter((li) => !li.hidden && !li.hasAttribute('data-fit-out')).length;
        g.hidden = vis === 0;
        const c = $('[data-seg-n]', g);
        if (c) c.textContent = vis;
      });

      /* ...and so do the segment chips (Filters, Handpiece parts...), so a chip never says 5 and shows 0 */
      segCounts(lis);

      const visible = lis.filter((li) => base(li) && !li.hasAttribute('data-fit-out')).length;
      setCount(visible, lis);
      emptyState(visible);
    }

    function commit({ fromPt = false } = {}) {
      /* The paint picker first: plp.js recounts when its sprayer changes, then apply() puts the fit count back */
      if (!fromPt) pushToPt();
      apply();
      toUrl();
      remember();
    }

    /* ---------- events ---------- */
    /* Panel checkboxes sit inside the storefront filter form: stop the change here so plp.js doesn't send the form */
    if (panel) {
      panel.addEventListener('change', (e) => {
        const i = e.target.closest('[data-ff-input]');
        if (!i) return;
        e.stopPropagation();
        const set = state[i.dataset.ffGroup];
        if (i.checked) set.add(i.value); else set.delete(i.value);
        commit();
      });
    }
    if (bar) {
      const sel = $('[data-ff-select]', bar);
      if (sel) {
        sel.addEventListener('change', (e) => {
          e.stopPropagation();
          if (sel.value === '__multi') return;
          state.model = sel.value ? new Set([sel.value]) : new Set();
          commit();
        });
      }
    }
    root.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-ff-chip]');
      if (chip) {
        const v = chip.dataset.ffChip;
        if (state.handpiece.has(v)) state.handpiece.delete(v); else state.handpiece.add(v);
        commit();
        return;
      }
      const rm = e.target.closest('[data-ff-remove]');
      if (rm) {
        const k = rm.dataset.ffRemove;
        const g = k.slice(0, k.indexOf(':'));
        state[g].delete(k.slice(g.length + 1));
        commit();
        return;
      }
      const sg = e.target.closest('[data-ff-seg]');
      if (sg) {
        const c = $$('[data-seg-chip]', root).find((x) => x.dataset.segChip === sg.dataset.ffSeg);
        if (c) c.click();
        return;
      }
      if (e.target.closest('[data-ff-clear]')) { GROUPS.forEach((g) => state[g].clear()); commit(); return; }
      /* The panel's and the applied row's Clear all clear the fit picks too */
      if (e.target.closest('.plp-filters__clear, .active__clear:not([data-ff-clear])')) {
        GROUPS.forEach((g) => state[g].clear());
        toUrl(); remember(); pushToPt();
      }
    });
    /* plp.js rewrites the shared count on every stock or segment change, even one that hides nothing: put ours back.
       These run after plp.js's own handlers on the same element (it registers first). */
    root.addEventListener('change', (e) => { if (e.target.matches('[data-stock-input], [data-seg-input]') && active()) apply(); });
    root.addEventListener('click', (e) => { if (e.target.closest('[data-seg-chip]') && active()) apply(); });
    /* Whenever plp.js hides or shows a card (stock, segments, the paint picker, however late its data arrives), recount.
       A MutationObserver runs before the next paint, so plp.js's own count never flashes. Only card attributes count:
       what apply() itself writes (data-fit-out, segment and empty-state hidden) is not watched, so it can't loop. */
    const results = $('[data-plp-region="results"]', root) || $('.plp-results', root);
    if (results && 'MutationObserver' in window) {
      new MutationObserver((muts) => {
        if (muts.some((m) => m.target.tagName === 'LI' && m.target.parentElement && m.target.parentElement.classList.contains('plp-grid'))) apply();
      }).observe(results, { subtree: true, attributes: true, attributeFilter: ['hidden', 'data-paint-out'] });
    }

    /* ---------- the rest of a collection that runs past one page ---------- */
    let gen = 0;
    async function loadAll() {
      const my = ++gen;
      const nav = $('.plp-results .plp-pages', root);
      const grid = $(`#PlpGrid-${sid}`, root);
      if (!nav || !grid) return;
      const nums = $$('.plp-pages__n', nav).map((el) => parseInt(el.textContent, 10)).filter((n) => n > 0);
      const cur = parseInt(($('.plp-pages__n.is-current', nav) || {}).textContent, 10) || 1;
      const last = Math.max(cur, ...nums);
      if (last > 8) return; /* far past anything here: leave Shopify's pages alone */
      root.classList.add('is-ff-loading');
      try {
        const pages = [];
        for (let p = 1; p <= last; p += 1) {
          if (p === cur) continue;
          const u = new URL(window.location.href);
          u.searchParams.set('page', p);
          u.searchParams.set('section_id', sid);
          const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          pages.push([p, $$(`#PlpGrid-${sid} > li`, doc)]);
          if (my !== gen) return;
        }
        const first = grid.firstElementChild;
        pages.forEach(([p, lis]) => lis.forEach((li) => {
          const node = document.importNode(li, true);
          if (p < cur) grid.insertBefore(node, first); else grid.appendChild(node);
        }));
        nav.remove();
        const u = new URL(window.location.href);
        if (u.searchParams.has('page')) { u.searchParams.delete('page'); history.replaceState(history.state, '', u.pathname + u.search + u.hash); }
        /* Let plp.js re-run its stock view over the new cards, then refilter */
        const st = $$('[data-stock-input]', root).find((i) => i.checked);
        if (st) st.dispatchEvent(new Event('change', { bubbles: true }));
        apply();
      } catch (err) {
        /* Keep Shopify's own pages if a fetch fails */
      } finally {
        if (my === gen) root.classList.remove('is-ff-loading');
      }
    }

    /* A sort, price or storefront filter change re-renders the grid through plp.js and pushes a URL without our params */
    document.addEventListener('ase:plp-updated', (e) => {
      if (e.detail?.root !== root) return;
      $$('[data-plp-region="fcount"]', root).forEach((el) => { delete el.dataset.ffBase; });
      toUrl();
      apply();
      loadAll();
    });
    window.addEventListener('popstate', () => {
      const u = fromUrl();
      state.model = u.model; state.handpiece = u.handpiece;
      setTimeout(apply, 0);
    });

    /* ---------- start: URL first, then this visit's picks (only those this page offers), then the paint picker ---------- */
    const u = fromUrl();
    if (u.has) {
      state.model = u.model; state.handpiece = u.handpiece;
    } else if (!machinePage) {
      const r = recalled();
      if (r) GROUPS.forEach((g) => { state[g] = new Set((r[g] || []).filter((v) => known(g, v) && cards().some((li) => fitsOf(li)[g].has(v)))); });
      /* A remembered pair that leaves this page empty: keep the sprayer alone, and if even that leaves nothing, start clean */
      const any = () => cards().some((li) => fitsAll(li));
      if (active() && !any()) { state.handpiece.clear(); if (!any()) state.model.clear(); }
    }
    if (!state.model.size && pt && !machinePage) { const m = ptModel(); if (m && known('model', m)) state.model.add(m); }
    if (active()) pushToPt();
    apply();
    if (active() || u.has) toUrl(); /* also drops a ?model= the page has no option for */
    if (u.has) remember();
    /* On a phone the chips scroll sideways: bring the first picked one into view (a shared ?handpiece= link) */
    $$('[data-ff-bar] .qchips__row', root).forEach((row) => {
      const on = $('[data-ff-chip].is-on', row);
      if (on && row.scrollWidth > row.clientWidth) row.scrollLeft = Math.max(0, on.offsetLeft - row.offsetLeft - 16);
    });
    loadAll();
  });
})();
